import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import SearchFilters from './components/SearchFilters';
import VenueFormModal from './components/VenueFormModal';
import VenueDetailModal from './components/VenueDetailModal';
import ConfirmModal from './components/ConfirmModal';
import { mockVenues, mockTenants, mockCreators } from './mockData';
import type { VenueDetail } from './types';

export default function VenuesPage() {
  const [venues, setVenues] = useState<VenueDetail[]>(mockVenues);
  const [filteredVenues, setFilteredVenues] = useState<VenueDetail[]>(mockVenues);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<VenueDetail | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: string; venue: VenueDetail | null }>({ type: '', venue: null });

  const handleSearch = (filters: any) => {
    let filtered = [...venues];

    if (filters.searchValue) {
      filtered = filtered.filter(venue => {
        if (filters.searchType === 'id') {
          return venue.id.toLowerCase().includes(filters.searchValue.toLowerCase());
        } else {
          return venue.name.toLowerCase().includes(filters.searchValue.toLowerCase());
        }
      });
    }

    if (filters.tenant) {
      filtered = filtered.filter(venue => venue.tenantId === filters.tenant);
    }

    if (filters.status) {
      filtered = filtered.filter(venue => venue.status === filters.status);
    }

    if (filters.province) {
      filtered = filtered.filter(venue => venue.address.province === filters.province);
    }

    if (filters.city) {
      filtered = filtered.filter(venue => venue.address.city === filters.city);
    }

    if (filters.creator) {
      filtered = filtered.filter(venue => {
        const creator = mockCreators.find(c => c.id === filters.creator);
        return creator && venue.createdBy === creator.name;
      });
    }

    setFilteredVenues(filtered);
  };

  const handleAdd = () => {
    setSelectedVenue(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (venue: VenueDetail) => {
    setSelectedVenue(venue);
    setIsFormModalOpen(true);
  };

  const handleViewDetail = (venue: VenueDetail) => {
    setSelectedVenue(venue);
    setIsDetailModalOpen(true);
  };

  const handleToggleStatus = (venue: VenueDetail) => {
    setConfirmAction({ type: 'toggle', venue });
    setIsConfirmModalOpen(true);
  };

  const handleFormSubmit = (formData: any) => {
    const tenant = mockTenants.find(t => t.id === formData.tenantId);
    
    if (selectedVenue) {
      const updatedVenues = venues.map(v => 
        v.id === selectedVenue.id 
          ? {
              ...v,
              name: formData.name,
              tenantId: formData.tenantId,
              tenantName: tenant?.name || '',
              address: {
                province: formData.province,
                city: formData.city,
                district: formData.district,
                detail: formData.detail
              },
              description: formData.description,
              area: parseFloat(formData.area),
              status: formData.status,
              enabledTime: formData.enabledTime,
              updatedAt: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false 
              }).replace(/\//g, '-'),
              updatedBy: '当前用户'
            }
          : v
      );
      setVenues(updatedVenues);
      setFilteredVenues(updatedVenues);
      alert('场所更新成功！');
    } else {
      const newVenue: VenueDetail = {
        id: `VEN${String(venues.length + 1).padStart(3, '0')}`,
        name: formData.name,
        tenantId: formData.tenantId,
        tenantName: tenant?.name || '',
        address: {
          province: formData.province,
          city: formData.city,
          district: formData.district,
          detail: formData.detail
        },
        description: formData.description,
        area: parseFloat(formData.area),
        status: formData.status,
        enabledTime: formData.enabledTime,
        thinClientCount: 0,
        createdAt: new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        }).replace(/\//g, '-'),
        createdBy: '当前用户',
        updatedAt: new Date().toLocaleString('zh-CN', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        }).replace(/\//g, '-'),
        updatedBy: '当前用户',
        thinClients: []
      };
      const updatedVenues = [...venues, newVenue];
      setVenues(updatedVenues);
      setFilteredVenues(updatedVenues);
      alert('场所创建成功！');
    }
    setIsFormModalOpen(false);
  };

  const handleConfirm = () => {
    if (confirmAction.type === 'toggle' && confirmAction.venue) {
      const newStatus = confirmAction.venue.status === 'enabled' ? 'disabled' : 'enabled';
      const updatedVenues = venues.map(v =>
        v.id === confirmAction.venue!.id
          ? { 
              ...v, 
              status: newStatus,
              updatedAt: new Date().toLocaleString('zh-CN', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false 
              }).replace(/\//g, '-'),
              updatedBy: '当前用户'
            }
          : v
      );
      setVenues(updatedVenues);
      setFilteredVenues(updatedVenues);
      alert(`场所已${newStatus === 'enabled' ? '启用' : '停用'}！`);
      setIsDetailModalOpen(false);
    }
    setIsConfirmModalOpen(false);
  };

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">场所管理</h1>
          <p className="text-sm text-gray-600 mt-1">管理云盒绑定场所，租户可查看场所信息</p>
        </div>

        <SearchFilters 
          onSearch={handleSearch}
          tenants={mockTenants}
          creators={mockCreators}
        />

        <div className="bg-white rounded-lg">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="text-sm text-gray-600">
              共 {filteredVenues.length} 条记录
            </div>
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-add-line"></i>
              新建场所
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">名称</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">地址</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">租户名称</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">瘦终端数量</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">面积</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">启用时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">创建时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">创建人</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">更新时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">更新人</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredVenues.map((venue) => (
                  <tr key={venue.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{venue.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{venue.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {venue.address.province} {venue.address.city} {venue.address.district} {venue.address.detail}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(venue)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          venue.status === 'enabled' ? 'bg-teal-600' : 'bg-gray-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            venue.status === 'enabled' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{venue.tenantName}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{venue.thinClientCount}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{venue.area} 平米</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.enabledTime || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.createdAt}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.createdBy}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.updatedAt}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{venue.updatedBy}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(venue)}
                          className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="编辑"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleViewDetail(venue)}
                          className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="详情"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(venue)}
                          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                            venue.status === 'enabled'
                              ? 'text-orange-600 hover:bg-orange-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={venue.status === 'enabled' ? '停用' : '启用'}
                        >
                          <i className={venue.status === 'enabled' ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <VenueFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSubmit={handleFormSubmit}
        venue={selectedVenue}
        tenants={mockTenants}
      />

      <VenueDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        venue={selectedVenue}
        onEdit={() => {
          setIsDetailModalOpen(false);
          setIsFormModalOpen(true);
        }}
        onToggleStatus={() => {
          if (selectedVenue) {
            handleToggleStatus(selectedVenue);
          }
        }}
      />

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        title={confirmAction.venue?.status === 'enabled' ? '停用场所' : '启用场所'}
        message={`确定要${confirmAction.venue?.status === 'enabled' ? '停用' : '启用'}场所"${confirmAction.venue?.name}"吗？`}
        type="warning"
      />
    </MainLayout>
  );
}
